import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import env from '../../config/env'
import { createPublicLink, listPublicLinks, revokePublicLink } from '../../service'
import {
  WATCH_LINK_ATTESTATION,
  WATCH_LINK_EXPLAINER,
  WATCH_LINK_CLIP_NOTE
} from '../../constants/sharing'

const EXPIRY_OPTIONS = [
  { value: '', label: 'No expiry' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' }
]

const expiresAtFor = days => days ? new Date(Date.now() + Number(days) * 86400000).toISOString() : null

const formatDate = value => value ? new Date(value).toLocaleDateString() : null

const isActive = (link, now) => !link.revoked_at && !(link.expires_at && new Date(link.expires_at).getTime() <= now)

const LinkRow = ({ link, name, onRevoke }) => (
  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
      {name ? <Typography variant="body2">{name}</Typography> : null}
      <Chip
        size="small"
        color={link.allow_playback ? 'warning' : 'default'}
        label={link.allow_playback ? 'Watch without sign-in' : 'Preview only'}
      />
      <Typography variant="caption" color="text.secondary">
        Created {formatDate(link.created_at)}
        {link.expires_at ? ` · expires ${formatDate(link.expires_at)}` : ''}
        {` · ${link.access_count} views`}
        {link.allow_playback && link.highlight_id && link.clip_status !== 'ready' ? ` · clip ${link.clip_status}` : ''}
      </Typography>
    </Stack>
    <Button size="small" color="error" onClick={() => onRevoke(link.id)}>Revoke</Button>
  </Stack>
)

LinkRow.propTypes = {
  link: PropTypes.object.isRequired,
  name: PropTypes.string,
  onRevoke: PropTypes.func.isRequired
}

ShareLinksDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  uploadId: PropTypes.string,
  highlightId: PropTypes.string,
  title: PropTypes.string,
  combinedFrom: PropTypes.arrayOf(PropTypes.object),
  lockedReason: PropTypes.string
}

/**
 * Owner-only management of hidden links (owner decision D14). Sharing stays
 * login-required by default; a link anyone can watch without signing in is an
 * explicit opt-in that can be revoked here.
 */
// lockedReason: why no new link can be made (a part combined into a full game video, D34); existing links can
// still be revoked.
export default function ShareLinksDialog ({ open, onClose, uploadId, highlightId = null, title, combinedFrom = null, lockedReason = null }) {
  const [links, setLinks] = useState([])
  // Links made on the parts a combined video replaced (D34 release B), so the owner can still revoke them.
  const [partLinks, setPartLinks] = useState([])
  const partIds = highlightId ? '' : (combinedFrom || []).map(part => part.uploadId).join(',')
  const [expiry, setExpiry] = useState('')
  const [created, setCreated] = useState(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // Interim minors policy (owner decision D7): confirm the right to share before opening a link.
  const [attested, setAttested] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const rows = await listPublicLinks({ uploadId, highlightId })
      const now = Date.now()
      setLinks((rows || []).filter(row => isActive(row, now) && (row.highlight_id || null) === (highlightId || null)))
    } catch (err) {
      console.error(err)
      setError('Unable to load shared links.')
    }
  }, [uploadId, highlightId])

  useEffect(() => {
    if (!open) return
    setCreated(null)
    setCopied(false)
    setError(null)
    setAttested(false)
    refresh()
  }, [open, refresh])

  // Every link on each part, the video's and its clips', with the part's name.
  const refreshPartLinks = async () => {
    const now = Date.now()
    const rows = await Promise.all((partIds ? combinedFrom : []).map(part => listPublicLinks({ uploadId: part.uploadId })
      .then(found => (found || []).filter(link => isActive(link, now)).map(link => ({
        ...link,
        partName: link.highlight_id ? `${part.name} (clip)` : part.name
      })))
      .catch(err => {
        console.error(err)
        return []
      })))
    setPartLinks(rows.flat())
  }

  useEffect(() => {
    if (open) refreshPartLinks()
  }, [open, partIds])

  const createWatchLink = async () => {
    setBusy(true)
    setError(null)
    try {
      const link = await createPublicLink({
        uploadId,
        highlightId,
        expiresAt: expiresAtFor(expiry),
        allowPlayback: true
      })
      const url = `${env.publicLinkUrl}${link.path}`
      setCreated({ url, clip: Boolean(highlightId) })
      try {
        await navigator.clipboard.writeText(url)
        setCopied(true)
      } catch (_) {
        setCopied(false)
      }
      await refresh()
    } catch (err) {
      console.error(err)
      const message = String(err.message || '')
      // 409: this part was combined into a full game video meanwhile; the API says so (D34).
      setError(err?.status === 409 && err.detail
        ? err.detail
        : message.includes(' 422')
          ? 'This video can\'t be shared without a login yet. Share the whole game, or use a preview link.'
          : message.includes(' 403')
            ? 'Only the video\'s owner can create a link that plays without signing in.'
            : message.includes(' 429')
              ? 'You have many active watch links. Revoke one before creating another.'
              : 'Unable to create the link. Nothing was shared.')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async id => {
    setError(null)
    try {
      await revokePublicLink(id)
      await Promise.all([refresh(), partIds ? refreshPartLinks() : null])
    } catch (err) {
      console.error(err)
      setError('Unable to revoke the link.')
    }
  }

  return (
    <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Shared links{title ? `: ${title}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box>
            <Typography variant="subtitle2" gutterBottom>Anyone with the link can watch (no sign-in)</Typography>
            <Typography variant="body2" color="text.secondary">
              {WATCH_LINK_EXPLAINER}
              {highlightId ? ` ${WATCH_LINK_CLIP_NOTE}` : ''}
            </Typography>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox size="small" checked={attested} onChange={ev => setAttested(ev.target.checked)} />}
              label={<Typography variant="body2">{WATCH_LINK_ATTESTATION}</Typography>}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <TextField
                select
                size="small"
                label="Expires"
                value={expiry}
                onChange={ev => setExpiry(ev.target.value)}
                sx={{ minWidth: 140 }}
              >
                {EXPIRY_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
              <Button variant="contained" onClick={createWatchLink} disabled={busy || !attested || Boolean(lockedReason)}>
                Create watch link
              </Button>
            </Stack>
            {lockedReason ? <Alert severity="info" sx={{ mt: 1.5 }}>{lockedReason}</Alert> : null}
            {created
              ? (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {copied ? 'Link copied.' : 'Link created. Copy it below.'} Anyone who has it can watch without signing in until you revoke it.
                  </Typography>
                  <TextField size="small" fullWidth value={created.url} InputProps={{ readOnly: true }} onFocus={ev => ev.target.select()} />
                </Alert>
                )
              : null}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Active links</Typography>
            {links.length
              ? (
                <Stack spacing={1}>
                  {links.map(link => <LinkRow key={link.id} link={link} onRevoke={revoke} />)}
                </Stack>
                )
              : <Typography variant="body2" color="text.secondary">No active links.</Typography>}
          </Box>

          {partLinks.length
            ? (
              <Box component="section" aria-label="Links to the original parts">
                <Typography variant="subtitle2" gutterBottom>Links to the original parts</Typography>
                <Stack spacing={1}>
                  {partLinks.map(link => <LinkRow key={link.id} link={link} name={link.partName} onRevoke={revoke} />)}
                </Stack>
              </Box>
              )
            : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
